
import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Star } from "lucide-react";

const New2Pricing = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Choisissez votre formule",
      subtitle: "Des options flexibles pour tous les budgets et objectifs",
      monthly: "par mois",
      popular: "Le plus populaire",
      getStarted: "Commencer",
      plans: [
        {
          name: "Essentiel",
          price: "29",
          description: "Parfait pour commencer votre première capsule temporelle",
          features: [
            "1 capsule temporelle",
            "Messages et photos illimités",
            "Investissement à partir de 25€/mois",
            "Sécurité bancaire suisse",
            "Support client par email"
          ]
        },
        {
          name: "Famille",
          price: "49",
          description: "Idéal pour les familles avec plusieurs enfants",
          features: [
            "3 capsules temporelles",
            "Messages vidéo illimités",
            "Investissement à partir de 25€/mois",
            "Invitations famille étendues",
            "Sécurité bancaire suisse",
            "Support client prioritaire",
            "Conseiller dédié"
          ],
          popular: true
        },
        {
          name: "Premium",
          price: "99",
          description: "Pour les familles qui veulent le maximum d'impact",
          features: [
            "Capsules temporelles illimitées",
            "Messages vidéo illimités",
            "Investissement à partir de 50€/mois",
            "Invitations famille étendues",
            "Sécurité bancaire suisse",
            "Support client VIP 24/7",
            "Conseiller dédié personnel",
            "Rapports détaillés trimestriels"
          ]
        }
      ]
    },
    en: {
      title: "Choose your plan",
      subtitle: "Flexible options for all budgets and goals",
      monthly: "per month",
      popular: "Most popular",
      getStarted: "Get started",
      plans: [
        {
          name: "Essential",
          price: "29",
          description: "Perfect to start your first time capsule",
          features: [
            "1 time capsule",
            "Unlimited messages and photos",
            "Investment from €25/month",
            "Swiss banking security",
            "Email customer support"
          ]
        },
        {
          name: "Family",
          price: "49",
          description: "Ideal for families with multiple children",
          features: [
            "3 time capsules",
            "Unlimited video messages",
            "Investment from €25/month",
            "Extended family invitations",
            "Swiss banking security",
            "Priority customer support",
            "Dedicated advisor"
          ],
          popular: true
        },
        {
          name: "Premium",
          price: "99",
          description: "For families who want maximum impact",
          features: [
            "Unlimited time capsules",
            "Unlimited video messages",
            "Investment from €50/month",
            "Extended family invitations",
            "Swiss banking security",
            "24/7 VIP customer support",
            "Personal dedicated advisor",
            "Detailed quarterly reports"
          ]
        }
      ]
    },
    de: {
      title: "Wählen Sie Ihren Plan",
      subtitle: "Flexible Optionen für alle Budgets und Ziele",
      monthly: "pro Monat",
      popular: "Am beliebtesten",
      getStarted: "Loslegen",
      plans: [
        {
          name: "Essential",
          price: "29",
          description: "Perfekt, um Ihre erste Zeitkapsel zu starten",
          features: [
            "1 Zeitkapsel",
            "Unbegrenzte Nachrichten und Fotos",
            "Investition ab 25€/Monat",
            "Schweizer Bankensicherheit",
            "E-Mail-Kundensupport"
          ]
        },
        {
          name: "Familie",
          price: "49",
          description: "Ideal für Familien mit mehreren Kindern",
          features: [
            "3 Zeitkapseln",
            "Unbegrenzte Videonachrichten",
            "Investition ab 25€/Monat",
            "Einladungen für erweiterte Familie",
            "Schweizer Bankensicherheit",
            "Prioritärer Kundensupport",
            "Persönlicher Berater"
          ],
          popular: true
        },
        {
          name: "Premium",
          price: "99",
          description: "Für Familien, die maximale Wirkung wollen",
          features: [
            "Unbegrenzte Zeitkapseln",
            "Unbegrenzte Videonachrichten",
            "Investition ab 50€/Monat",
            "Einladungen für erweiterte Familie",
            "Schweizer Bankensicherheit",
            "24/7 VIP-Kundensupport",
            "Persönlicher Berater",
            "Detaillierte Quartalsberichte"
          ]
        }
      ]
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {content[language].title}
          </h2>
          <p className="text-xl text-gray-600">
            {content[language].subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {content[language].plans.map((plan, index) => (
            <Card 
              key={index}
              className={`relative ${plan.popular ? 'border-2 border-legatia-orange scale-105 shadow-xl' : 'border border-gray-200 shadow-sm'} bg-white hover:shadow-lg transition-all duration-300`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-legatia-orange to-legatia-red text-white px-4 py-1">
                    <Star className="h-3 w-3 mr-1" />
                    {content[language].popular}
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-8">
                <CardTitle className="text-2xl font-bold mb-2">{plan.name}</CardTitle>
                <div className="mb-4">
                  <span className="text-4xl font-bold">€{plan.price}</span>
                  <span className="text-gray-600 ml-2">{content[language].monthly}</span>
                </div>
                <p className="text-gray-600">{plan.description}</p>
              </CardHeader>
              
              <CardContent className="space-y-6">
                <ul className="space-y-4">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <Check className="h-5 w-5 text-legatia-green mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button 
                  className={`w-full py-6 text-lg rounded-full ${
                    plan.popular 
                      ? 'bg-gradient-to-r from-legatia-orange to-legatia-red hover:from-legatia-orange/90 hover:to-legatia-red/90 text-white' 
                      : 'border-2 border-legatia-orange text-legatia-orange hover:bg-legatia-orange hover:text-white'
                  }`}
                  variant={plan.popular ? "default" : "outline"}
                  onClick={() => window.location.href = "https://app.getlegatia.ch/"}
                >
                  {content[language].getStarted}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default New2Pricing;
